<template>
  <div class="popular-categories-container">
    <!-- ✅ LOADING STATE: Show while fetching listings -->
    <div v-if="isLoading" class="loading-container">
      <div class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        <p class="mt-4 text-grey">Loading popular categories...</p>
      </div>
    </div>

    <!-- ✅ ERROR STATE: Show if there's an error -->
    <div v-else-if="hasError" class="error-container">
      <v-alert type="error" class="mb-4">
        {{ error }}
      </v-alert>
      <v-btn color="primary" @click="initializeComponent" variant="outlined"> Try Again </v-btn>
    </div>

    <!-- ✅ EMPTY STATE: Show if no categories have listings -->
    <div v-else-if="categoriesWithListings.length === 0" class="empty-container">
      <div class="text-center py-8">
        <v-icon size="64" color="grey">mdi-inbox-outline</v-icon>
        <h3 class="mt-4 mb-2">No Category Listings Available</h3>
        <p class="text-grey">Check back later for new listings in popular categories.</p>
      </div>
    </div>

    <!-- ✅ MAIN CONTENT: Show categories with listings -->
    <div v-else>
      <!-- ✅ SECTION HEADER: Summary information -->
      <div class="section-summary mb-6">
       
        <p class="text-grey" v-if="showDebugInfo">
          Showing {{ categoriesWithListings.length }} categories with {{ totalListingsCount }} total
          listings
        </p>
      </div>

      <!-- ✅ CATEGORY SECTIONS: Loop through categories that have listings -->
      <div
        v-for="(categoryData, index) in categoriesWithListings"
        :key="categoryData.category_id"
        class="category-section mb-8"
      >
        <SlidingScrollableListingComponent
          :title="`Popular in ${categoryData.category_name}`"
          :titleClasses="`category-title font-secondary text-lg font-semibold category-${
            index + 1
          }`"
          :listings="categoryData.listings"
          :loading="false"
          :error="null"
          :favorites="favorites"
          :seeMoreLink="getSafeCategoryLink(categoryData)"
          :seeMoreText="
            categoryData.total_count > props.maxListingsPerCategory
              ? `View all ${categoryData.category_name} listings (${categoryData.total_count})`
              : null
          "
          :emptyMessage="`No ${categoryData.category_name.toLowerCase()} listings available`"
          emptyActionText="Browse all categories"
          :emptyActionLink="{ name: 'Home' }"
          :useTestData="false"
          sectionType="category"
          :showDebugInfo="showDebugInfo"
          @viewDetails="handleViewDetails"
          @toggleFavorite="handleToggleFavorite"
          @retry="() => refreshCategoryListings(categoryData)"
        />
      </div>

      <!-- ✅ LOAD MORE BUTTON: Show if there are more categories -->
      <div v-if="hasMoreCategories" class="load-more-section text-center mt-6">
        <v-btn
          @click="loadMoreCategories"
          :loading="loadingMore"
          color="primary"
          variant="outlined"
          size="large"
        >
          Load More Categories
        </v-btn>
      </div>
    </div>

    <!-- ✅ DEBUG PANEL: Development information (optional) -->
    <div
      v-if="showDebugInfo"
      class="debug-panel mt-6 pa-4"
      style="background: #f5f5f5; border-radius: 8px"
    >
      <h4>Debug Information</h4>
      <div
        class="debug-grid"
        style="
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-top: 16px;
        "
      >
        <div>
          <strong>Total Categories:</strong> {{ categoriesWithListings.length }}<br />
          <strong>Has Data:</strong> {{ isDataReady ? 'Yes' : 'No' }}<br />
          <strong>Is Loading:</strong> {{ isLoading ? 'Yes' : 'No' }}
        </div>
        <div>
          <strong>HomeStore Listings:</strong> {{ homeListings.length }}<br />
          <strong>Total Combined:</strong> {{ totalListingsCount }}<br />
          <strong>Categories Count:</strong> {{ categoriesWithListings.length }}
        </div>
        <div>
          <strong>Has Error:</strong> {{ hasError ? 'Yes' : 'No' }}<br />
          <strong>Use Test Data:</strong> {{ useTestData ? 'Yes' : 'No' }}<br />
          <strong>Data Source:</strong> HomeListingsStore Only
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import SlidingScrollableListingComponent from './SlidingScrollableListingComponent.vue';

// ✅ PROPS: Component configuration
const props = defineProps({
  favorites: { type: Array, default: () => [] },
  useTestData: { type: Boolean, default: false },
  dataReady: { type: Boolean, default: false },
  maxCategoriesPerPage: { type: Number, default: 6 },
  maxListingsPerCategory: { type: Number, default: 8 },
  showDebugInfo: { type: Boolean, default: false },
});

// ✅ EMITS: Component events
const emit = defineEmits(['viewDetails', 'toggleFavorite']);

// ✅ SETUP: Store and router
const store = useStore();
const router = useRouter();

// ✅ STATE: Component reactive state
const loadingMore = ref(false);
const currentPageSize = ref(props.maxCategoriesPerPage);

// ✅ COMPUTED: ONLY use HomeListingsStore (completely isolated from other stores)
const homeListings = computed(() => {
  return store.getters['homeListings/allListings'] || [];
});

const categorizedListings = computed(() => {
  return store.getters['homeListings/categorizedListings'] || [];
});

const isLoading = computed(() => {
  return store.getters['homeListings/isLoading'];
});

const hasError = computed(() => {
  return store.getters['homeListings/hasError'];
});

const error = computed(() => {
  return store.getters['homeListings/error'];
});

const summary = computed(() => {
  return store.getters['homeListings/summary'] || {};
});

const isDataReady = computed(() => {
  return store.getters['homeListings/hasData'] && !isLoading.value;
});

// ✅ ADD: Job listing detection function (consistent with other components)
const isJobListing = (listing) => {
  // Check if category ID is for jobs (usually 2) or subcategory ID is for jobs (16)
  return (
    listing.category_id === 2 ||
    listing.category?.id === 2 ||
    listing.sub_category?.id === 16 ||
    listing.subcategory?.id === 16 ||
    (listing.category?.name && listing.category.name.toLowerCase().includes('job'))
  );
};

// ✅ ADD: Filter out "I want a job" subcategory (ID 17) from all listings
const filteredListings = computed(() => {
  return homeListings.value.filter((listing) => {
    // Exclude "I want a job" subcategory (ID 17)
    const subcategoryId = listing.sub_category?.id || listing.subcategory?.id;
    return subcategoryId !== 17 && subcategoryId !== '17';
  });
});

// ✅ MODIFIED: Categories with listings (from HomeListingsStore only)
const categoriesWithListings = computed(() => {
  const categories = categorizedListings.value.slice(0, currentPageSize.value);

  const result = categories
    .map((category) => {
      // Filter out "I want a job" subcategory (ID 17) from listings
      const filteredCategoryListings = category.listings.filter((listing) => {
        const subcategoryId = listing.sub_category?.id || listing.subcategory?.id;
        return subcategoryId !== 17 && subcategoryId !== '17';
      });

      return {
        category_id: category.category_id,
        category_name: category.category_name,
        listings: filteredCategoryListings.slice(0, props.maxListingsPerCategory),
        total_count: filteredCategoryListings.length,
        hasRealListings: filteredCategoryListings.length > 0,
      };
    })
    .filter((categoryData) => categoryData.hasRealListings);

  return result;
});

// ✅ COMPUTED: Pagination and summary
const hasMoreCategories = computed(() => {
  const totalCategories = categorizedListings.value.length;
  return currentPageSize.value < totalCategories;
});

const totalListingsCount = computed(() => {
  return categoriesWithListings.value.reduce((total, categoryData) => {
    return total + categoryData.total_count;
  }, 0);
});

// ✅ METHODS: Component functionality

/**
 * Initialize component data
 */
const initializeComponent = async () => {
  try {
    await store.dispatch('homeListings/refresh');
  } catch (error) {
    console.error('❌ Failed to initialize component:', error);
  }
};

/**
 * Load more categories
 */
const loadMoreCategories = async () => {
  loadingMore.value = true;

  try {
    await new Promise((resolve) => setTimeout(resolve, 500));
    currentPageSize.value += props.maxCategoriesPerPage;
  } catch (err) {
    console.error('Failed to load more categories:', err);
  } finally {
    loadingMore.value = false;
  }
};

/**
 * Refresh listings for a specific category
 */
const refreshCategoryListings = async (categoryData) => {
  try {
    await store.dispatch('homeListings/refresh');
  } catch (err) {
    console.error(`❌ Failed to refresh category ${categoryData.category_name}:`, err);
  }
};

/**
 * Generate safe category link
 */
const getSafeCategoryLink = (categoryData) => {
  if (categoryData.total_count <= props.maxListingsPerCategory) {
    return null;
  }

  try {
    return {
      name: 'searchResults',
      query: {
        category: categoryData.category_id,
        categoryName: categoryData.category_name,
      },
    };
  } catch (error) {
    console.warn('Failed to generate safe category link:', error);
    return null;
  }
};

// ✅ EVENT HANDLERS: User interactions
const handleViewDetails = (listing) => {
  emit('viewDetails', listing);
};

const handleToggleFavorite = (listing) => {
  emit('toggleFavorite', listing);
};

// ✅ LIFECYCLE: Component initialization
onMounted(async () => {
  // Only initialize if we don't have data
  if (!isDataReady.value && !isLoading.value) {
    await initializeComponent();
  }
});

// ✅ WATCHERS: Reactive updates
watch(
  () => props.dataReady,
  (newValue) => {
    if (newValue && !isDataReady.value && !isLoading.value) {
      console.log('🔄 External dataReady trigger - refreshing HomeListingsStore');
      store.dispatch('homeListings/refresh');
    }
  }
);

// ✅ DEBUG: Log data changes
if (props.showDebugInfo) {
  watch(
    [homeListings, categorizedListings, isLoading],
    ([newListings, newCategorized, newLoading]) => {
      console.log('📊 PopularCategoryListings Debug (HomeListingsStore only):', {
        totalListings: newListings.length,
        categoriesCount: newCategorized.length,
        isLoading: newLoading,
        summary: summary.value,
      });
    },
    { immediate: true }
  );
}
</script>

<style scoped>
.popular-categories-container {
  width: 100%;
  min-height: 200px;
}

.section-summary {
  padding: 16px 0;
}

.category-section {
  margin-bottom: 48px;
}

.category-section:last-child {
  margin-bottom: 24px;
}

.category-title {
  color: #2c3e50;
}

/* ✅ ENHANCED: Category-specific title colors with better contrast */
.category-section:nth-child(1) .category-1 {
  color: #e74c3c;
}
.category-section:nth-child(2) .category-2 {
  color: #3498db;
}
.category-section:nth-child(3) .category-3 {
  color: #2ecc71;
}
.category-section:nth-child(4) .category-4 {
  color: #f39c12;
}
.category-section:nth-child(5) .category-5 {
  color: #9b59b6;
}
.category-section:nth-child(6) .category-6 {
  color: #1abc9c;
}
.category-section:nth-child(7) .category-7 {
  color: #e67e22;
}
.category-section:nth-child(8) .category-8 {
  color: #34495e;
}

/* ✅ LOADING STATE: Better loading appearance */
.loading-container {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
  border-radius: 8px;
  border: 1px dashed #ddd;
}

/* ✅ ERROR STATE: Improved error display */
.error-container {
  padding: 32px;
  text-align: center;
  background-color: #fef7f7;
  border-radius: 8px;
  border: 1px solid #fecaca;
}

/* ✅ EMPTY STATE: Better empty state design */
.empty-container {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
  border-radius: 8px;
  border: 1px dashed #ddd;
  padding: 48px 24px;
}

/* ✅ LOAD MORE: Improved button section */
.load-more-section {
  padding: 32px 0;
  border-top: 1px solid #e0e0e0;
  background-color: #fafafa;
  border-radius: 8px;
  margin-top: 24px;
}

/* ✅ DEBUG PANEL: Development information styling */
.debug-panel {
  border: 2px dashed #ccc;
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.debug-grid {
  font-size: 11px;
  line-height: 1.4;
}

/* ✅ RESPONSIVE: Mobile optimizations */
/* Media queries */
@media (max-width: 768px) {
  .category-title {
    font-size: var(--font-size-base) !important; /* Reduced from 18px to 16px */
  }
}

@media (max-width: 480px) {
  .category-title {
    font-size: var(--font-size-sm) !important; /* Reduced from 16px to 14px */
  }
}

@media (max-width: 768px) {
  .category-section {
    margin-bottom: 32px;
  }

  .section-summary {
    padding: 12px 0;
  }

  .category-title {
    font-size: 18px !important;
  }

  .debug-grid {
    grid-template-columns: 1fr !important;
  }
}

@media (max-width: 480px) {
  .popular-categories-container {
    padding: 0 8px;
  }

  .category-title {
    font-size: 16px !important;
  }

  .load-more-section {
    padding: 24px 16px;
  }
}

/* ✅ ADD: Job listing specific styles */
.job-listing-card {
  border-left: 4px solid #3949ab !important; /* Blue accent line */
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.job-listing-header {
  padding: 12px 16px 8px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.job-badges {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.job-content {
  padding: 16px;
}

.job-title {
  font-size: var(--font-size-base) !important;
  font-weight: var(--font-weight-bold) !important;
  margin-bottom: 8px !important;
  color: #333 !important;
  line-height: 1.3 !important;
}

.company-name {
  font-size: var(--font-size-sm) !important;
  color: #555;
  font-weight: var(--font-weight-medium) !important;
  margin-bottom: 12px;
  letter-spacing: 0.01em;
}

.job-details-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin: 12px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  padding: 12px 0;
}

.job-detail-item {
  display: flex;
  flex-direction: column;
}

.job-detail-label {
  font-size: 0.6rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.job-detail-value {
  font-size: 0.75rem;
  color: #333;
  font-weight: 500;
}

/* ✅ ENHANCED: Badge styles for job listings */
.feature-badge {
  position: static;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
  font-size: 9px;
  font-weight: 600;
  padding: 3px 6px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 3px;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.verified-badge {
  position: static;
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  color: white;
  font-size: 9px;
  font-weight: 600;
  padding: 3px 6px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 3px;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* ✅ RESPONSIVE: Job details for mobile */
@media (max-width: 600px) {
  .job-details-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .job-detail-label {
    font-size: 0.55rem;
  }

  .job-detail-value {
    font-size: 0.7rem;
  }

  .job-title {
    font-size: var(--font-size-sm) !important;
  }

  .company-name {
    font-size: var(--font-size-xs) !important;
  }
}

/* Remove shadows and borders for category boxes on mobile only */
@media (max-width: 768px) {
  .popular-categories-container .v-card {
    box-shadow: none !important;
    border: none !important;
   
  }
}

</style>
